import { FileSignature, Loader2, Receipt } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Field, FormSection } from '@/components/common/FormBits'
import { Button } from '@/components/ui/button'
import { DateField } from '@/components/ui/date-field'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { NumberInput } from '@/components/ui/number-input'
import { Select } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { toastError } from '@/features/finance/ui'
import { useSaveServerPayment, useUpdateServer } from '../api'
import type { InfraServer, ServerPayment, ServerPaymentInput } from '../types'

const today = () => new Date().toISOString().slice(0, 10)
const addMonths = (iso: string, n: number) => {
  const d = new Date(`${iso}T00:00:00Z`)
  d.setUTCMonth(d.getUTCMonth() + n)
  return d.toISOString().slice(0, 10)
}

function Footer({ pending, onCancel }: { pending: boolean; onCancel: () => void }) {
  const { t } = useTranslation()
  return (
    <>
      <Button type="button" variant="cancel" onClick={onCancel}>
        {t('common.cancel')}
      </Button>
      <Button type="submit" disabled={pending}>
        {pending ? <Loader2 className="animate-spin" /> : null}
        {t('common.save')}
      </Button>
    </>
  )
}

export function ServerPaymentFormModal({ server, payment, onDone }: { server: InfraServer; payment: ServerPayment | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveServerPayment()
  const step = server.billingCycle === 'yearly' ? 12 : 1
  const [f, setF] = useState<ServerPaymentInput>(() => {
    if (payment) {
      const { id: _i, serverId: _s, ...rest } = payment
      return rest
    }
    const start = server.nextRenewalAt || today()
    return { date: today(), periodStart: start, periodEnd: addMonths(start, step), amount: (Number(server.monthlyCost) * step).toFixed(2), invoiceNo: '', method: server.paymentMethod, status: 'paid', note: '' }
  })
  const [error, setError] = useState('')
  const set = <K extends keyof ServerPaymentInput>(k: K, v: ServerPaymentInput[K]) => setF((p) => ({ ...p, [k]: v }))

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!f.amount || Number(f.amount) <= 0) return setError(t('admin.infra.payment.amountRequired'))
    if (f.status === 'paid' && !f.date) return setError(t('admin.infra.payment.dateRequired'))
    save.mutate(
      { serverId: server.id, id: payment?.id, input: f },
      {
        onSuccess: () => {
          toast.success(t('common.saved'))
          onDone()
        },
        onError: toastError,
      },
    )
  }

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<Receipt />}
      title={payment ? t('admin.infra.payment.editTitle') : t('admin.infra.payment.createTitle')}
      description={t('admin.infra.payment.hint', { name: server.name, plan: server.plan })}
      onSubmit={submit}
      footer={<Footer pending={save.isPending} onCancel={onDone} />}
    >
      <FormSection>
        <Field id="sp-start" label={t('admin.infra.payment.periodStart')}>
          <DateField id="sp-start" value={f.periodStart} onChange={(v) => setF((p) => ({ ...p, periodStart: v, periodEnd: payment ? p.periodEnd : addMonths(v, step) }))} clearable={false} />
        </Field>
        <Field id="sp-end" label={t('admin.infra.payment.periodEnd')}>
          <DateField id="sp-end" value={f.periodEnd} onChange={(v) => set('periodEnd', v)} clearable={false} min={f.periodStart} />
        </Field>
        <Field id="sp-amount" label={t('admin.infra.payment.amount')}>
          <NumberInput id="sp-amount" value={f.amount} onChange={(v) => set('amount', v)} minDecimalsOnBlur={2} suffix="₺" />
        </Field>
        <Field id="sp-invoice" label={t('admin.infra.payment.invoiceNo')}>
          <Input id="sp-invoice" value={f.invoiceNo} onChange={(e) => set('invoiceNo', e.target.value.toUpperCase())} className="font-mono" maxLength={30} />
        </Field>
        <Field id="sp-status" label={t('admin.common.status')}>
          <Select id="sp-status" value={f.status} onChange={(v) => set('status', v)} options={(['paid', 'pending', 'overdue'] as const).map((s) => ({ value: s, label: t(`admin.infra.payment.status.${s}`) }))} />
        </Field>
        <Field id="sp-method" label={t('admin.infra.payment.method')}>
          <Select id="sp-method" value={f.method} onChange={(v) => set('method', v)} options={(['card', 'transfer'] as const).map((m) => ({ value: m, label: t(`admin.infra.paymentMethod.${m}`) }))} />
        </Field>
        {f.status === 'paid' ? (
          <Field id="sp-date" label={t('admin.infra.payment.paidAt')}>
            <DateField id="sp-date" value={f.date} onChange={(v) => set('date', v)} clearable={false} />
          </Field>
        ) : null}
        <Field id="sp-note" label={t('admin.infra.payment.note')} full>
          <Textarea id="sp-note" rows={2} maxLength={200} value={f.note} onChange={(e) => set('note', e.target.value)} />
        </Field>
        {error ? <p className="text-xs text-destructive sm:col-span-2">{error}</p> : null}
      </FormSection>
    </Modal>
  )
}

type ContractInput = Pick<InfraServer, 'plan' | 'billingCycle' | 'monthlyCost' | 'contractNo' | 'nextRenewalAt' | 'paymentMethod' | 'startedAt' | 'notes'>

export function ServerContractFormModal({ server, onDone }: { server: InfraServer; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useUpdateServer()
  const [f, setF] = useState<ContractInput>({
    plan: server.plan,
    billingCycle: server.billingCycle,
    monthlyCost: server.monthlyCost,
    contractNo: server.contractNo,
    nextRenewalAt: server.nextRenewalAt,
    paymentMethod: server.paymentMethod,
    startedAt: server.startedAt,
    notes: server.notes,
  })
  const set = <K extends keyof ContractInput>(k: K, v: ContractInput[K]) => setF((p) => ({ ...p, [k]: v }))

  const submit = (e: React.FormEvent) => {
    e.preventDefault()
    save.mutate(
      { id: server.id, input: f },
      {
        onSuccess: () => {
          toast.success(t('common.saved'))
          onDone()
        },
        onError: toastError,
      },
    )
  }

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      icon={<FileSignature />}
      title={t('admin.infra.contract.editTitle', { name: server.name })}
      description={t('admin.infra.contract.hint')}
      onSubmit={submit}
      footer={<Footer pending={save.isPending} onCancel={onDone} />}
    >
      <FormSection>
        <Field id="sc-plan" label={t('admin.infra.contract.plan')}>
          <Input id="sc-plan" value={f.plan} onChange={(e) => set('plan', e.target.value)} maxLength={60} />
        </Field>
        <Field id="sc-no" label={t('admin.infra.contract.contractNo')}>
          <Input id="sc-no" value={f.contractNo} onChange={(e) => set('contractNo', e.target.value)} className="font-mono" maxLength={30} />
        </Field>
        <Field id="sc-start" label={t('admin.infra.contract.startedAt')}>
          <DateField id="sc-start" value={f.startedAt} onChange={(v) => set('startedAt', v)} clearable={false} />
        </Field>
        <Field id="sc-renew" label={t('admin.infra.contract.nextRenewalAt')}>
          <DateField id="sc-renew" value={f.nextRenewalAt} onChange={(v) => set('nextRenewalAt', v)} clearable={false} />
        </Field>
        <Field id="sc-cycle" label={t('admin.infra.contract.billingCycle')}>
          <Select id="sc-cycle" value={f.billingCycle} onChange={(v) => set('billingCycle', v)} options={(['monthly', 'yearly'] as const).map((c) => ({ value: c, label: t(`admin.infra.billingCycle.${c}`) }))} />
        </Field>
        <Field id="sc-cost" label={t('admin.infra.contract.monthlyCost')} hint={t('admin.infra.contract.monthlyCostHint')}>
          <NumberInput id="sc-cost" value={f.monthlyCost} onChange={(v) => set('monthlyCost', v)} minDecimalsOnBlur={2} suffix="₺" />
        </Field>
        <Field id="sc-method" label={t('admin.infra.payment.method')}>
          <Select id="sc-method" value={f.paymentMethod} onChange={(v) => set('paymentMethod', v)} options={(['card', 'transfer'] as const).map((m) => ({ value: m, label: t(`admin.infra.paymentMethod.${m}`) }))} />
        </Field>
        <Field id="sc-notes" label={t('admin.infra.contract.notes')} full>
          <Textarea id="sc-notes" rows={3} maxLength={300} value={f.notes} onChange={(e) => set('notes', e.target.value)} />
        </Field>
      </FormSection>
    </Modal>
  )
}
